import type { EquipmentManual } from '@/types';

// 电解铝车间设备操作手册库 v2
// 覆盖：CELL-101 (代表电解槽) / POT-CTRL-101 (槽控柜) / TRA-301 / FGT-301 / HMI-301 / CTRL-401
export const aluminumManuals: Record<string, EquipmentManual> = {
  'CELL-101': {
    overview:
      '#101 电解槽是 500 kA 大型预焙阳极电解槽，槽寿命 5-8 年。' +
      '内衬 + 阴极碳块 + 槽周壁 + 阳极 26-32 块，注入约 30 t 冰晶石熔体 + 12 t 铝水。' +
      '日产铝约 3.0 t，电耗 13000 kWh/t。是车间核心设备。',
    operatingProcedure: [
      '每 4 小时巡检一次，记录电压、温度、声音、火苗颜色',
      '每天定时下料 4-10 次，根据浓度反馈调节频次',
      '每 24-32 小时换极一次（按阳极消耗排班）',
      '每周校核分子比和铝水高度（取样化验）',
      '每月校验热平衡（炉帮厚度 + 阳极电流分布）',
      '出铝按计划 1-2 次/天，留底铝 18 cm',
      '换极后 4 小时内电压偏高属正常，>4 小时仍偏高要查极距',
    ],
    safetyNotes: [
      '槽顶 950°C 高温 + 1660V 直流强电，禁止铁器/工具掉入',
      '强直流磁场，禁止戴金属表/项链作业',
      '槽边踩稳防滑，溅入熔体瞬间烧伤',
      '阳极效应时严禁靠近吹氟，戴防毒面具',
      '漏炉征兆：母线 > 90°C / 槽底有红斑 → 立即上报撤离',
    ],
    troubleshooting: [
      { symptom: '电压突跳 > 10V', action: '阳极效应！下料 + 降极距 + 吹氮气' },
      { symptom: '电压 < 4.0V', action: '极距过短，上提阳极或抽铝水' },
      { symptom: '电解质温度 < 935°C', action: '冷槽风险，加纯碱调分子比 + 加电流' },
      { symptom: '电解质温度 > 970°C', action: '过热，降电流或加 AlF₃' },
      { symptom: '槽噪声 > 80 mV', action: '氧化铝快没了，立即下料' },
      { symptom: '铝水高度 > 25 cm', action: '出铝降液面，警惕滚铝' },
    ],
  },

  'BUS-101': {
    overview:
      '直流母线 BUS-101 是连接整流变压器和 4 台电解槽的大电流汇流装置，' +
      '由 200 mm × 50 mm 铝排或铜排焊接而成，承载 500 kA。母线温升是漏炉的关键预警指标。',
    operatingProcedure: [
      '每班巡检红外测温，确认各接头温度 < 85°C',
      '每周紧固一次所有母线连接螺栓',
      '每月做一次接触电阻测试（毫欧级）',
      '检查母线支架与地面绝缘电阻 > 5 MΩ',
    ],
    safetyNotes: [
      '母线带 1660V 直流 + 500 kA，靠近会有触电+电弧风险',
      '禁止单手作业，禁止铁器接触',
      '检修必须切断系列电源 + 验电 + 接地',
    ],
    troubleshooting: [
      { symptom: '母线温度 > 90°C 突升', action: '⚠️ 槽底漏炉征兆！立即停槽切电源' },
      { symptom: '某段温度局部高', action: '接头松动，拧紧螺栓 + 清洁接触面' },
      { symptom: '母线电压偏低', action: '检查整流档位 / 二次电压是否正常' },
    ],
  },

  'TRA-301': {
    overview:
      '整流变压器 TRA-301 把厂区 35 kV 高压交流降到合适电压并通过晶闸管整流为 1620-1700V 直流，' +
      '驱动 4 台电解槽串联运行。容量 80 MVA，配 32 档调压机构。',
    operatingProcedure: [
      '开机前检查油位、油温（< 75°C）、压力释放阀',
      '档位调节遵循"先粗调档位，再细调电抗"',
      '调档前确认无负荷电流冲击',
      '每年大保养：吊罩 + 换油 + 检测绝缘',
    ],
    safetyNotes: [
      '一次侧 35 kV 高压，操作必须双人复诵',
      '调档时严禁跳挡（一次 > 2 挡），冲击电流损坏阳极',
      '油温 > 90°C 自动跳闸，禁止屏蔽',
    ],
    troubleshooting: [
      { symptom: '二次电压偏离设定 > 20V', action: '检查档位机构、晶闸管触发' },
      { symptom: '油温 > 80°C', action: '检查冷却器风扇 / 油泵 / 油位' },
      { symptom: '档位卡死', action: '切手动模式调节，联系电气班检修' },
    ],
  },

  'CRA-301': {
    overview:
      '阳极天车 CRA-301 是行车式吊装设备，负责换极、调极、加料辅助。' +
      '最大提升力 25 t，行走范围覆盖整个电解车间。配阳极导电棒夹具和残极抓斗。',
    operatingProcedure: [
      '每班开机检查制动器、限位开关、起升钢丝绳',
      '换极前确认目标阳极位置 + 电流分布异常',
      '残极抓斗夹紧后慢提，避免熔体溅出',
      '新阳极下放速度 < 0.2 m/min，对准导电棒位置',
      '每月清理夹具碳渣',
    ],
    safetyNotes: [
      '换极区域下方禁止有人',
      '吊装时禁止超载报警继续作业',
      '阳极残极 800°C 高温，戴防火罩 + 手套',
      '钢丝绳磨损 > 10% 立即更换',
    ],
    troubleshooting: [
      { symptom: '提升过载报警', action: '可能阳极冻结，振动 + 加大提升力分段试' },
      { symptom: '行车走偏', action: '检查导轨润滑和水平' },
      { symptom: '夹具不夹', action: '检查液压油压力和夹爪机构' },
    ],
  },

  'FGT-301': {
    overview:
      '烟气净化系统 FGT-301 采用干法氟化铝吸附 + 布袋除尘工艺，' +
      '回收 HF 气体（重新用于电解），HF 排放 < 3 mg/m³，含尘 < 15 mg/m³。' +
      '是环保红线设备。',
    operatingProcedure: [
      '每班记录引风机频率、负压、HF 在线监测值',
      '每周清理布袋（脉冲清灰）',
      '每月化验氟化铝吸附剂活性',
      '每季度更换布袋滤料',
    ],
    safetyNotes: [
      'HF 强腐蚀，作业戴防毒面具 + 耐酸手套',
      '烟道高温 100-150°C，禁止徒手接触',
      '布袋检修须切断主风机并通风至 < 30°C',
    ],
    troubleshooting: [
      { symptom: 'HF 浓度 > 3 mg/m³', action: '提高风机频率 + 检查吸附剂' },
      { symptom: '含尘 > 15 mg/m³', action: '布袋破损或脉冲清灰失效，停机检查' },
      { symptom: '负压不足', action: '风机故障或管路漏风' },
    ],
  },

  'POT-202': {
    overview:
      '铝水抬包 POT-202 是真空抽吸式出铝设备，5 t 容量。' +
      '从电解槽底部插入耐火嘴，抽真空把 920°C 铝水吸入抬包，再转运到铸锭机。',
    operatingProcedure: [
      '出铝前检查抬包内壁耐火砖 + 密封圈',
      '抽吸前确认真空度能拉到 -85 kPa 以下',
      '插入吸嘴时缓慢，避免损伤槽内炉帮',
      '抽吸时间 8-12 分钟，过快易卷电解质',
      '出铝完成 → 转运 → 倾倒铸锭机',
      '抬包每 50 次出铝后大保养',
    ],
    safetyNotes: [
      '铝水 920°C 高温，转运区禁止人员',
      '真空泄漏会喷溅铝水，提高警觉',
      '抬包吊装重 5 t+ 铝水共 8 t，吊具必须验收',
      '禁止在抬包嘴方向站人',
    ],
    troubleshooting: [
      { symptom: '真空度 > -60 kPa', action: '密封圈或管路漏，立即检查' },
      { symptom: '抽出物有杂质', action: '吸嘴插入过深卷入电解质' },
      { symptom: '抬包温度异常', action: '保温衬里损坏或铝水温度低' },
    ],
  },

  'FEED-201': {
    overview:
      '打壳下料器 FEED-201 是定时打破电解质表面结壳并精确投入氧化铝粉的关键设备，' +
      '配点式下料 + 气缸打壳锤。每台槽配 4-6 个下料点。',
    operatingProcedure: [
      '开机前检查气缸压力 0.5-0.7 MPa',
      '设定打壳频次：常规 6 次/h，浓度低时加密',
      '每周清理下料口结块',
      '下料量校准：每次 2-3 kg',
      '打壳锤每月检查磨损，> 30% 更换',
    ],
    safetyNotes: [
      '打壳锤动作有冲击力，禁止下方有人',
      '槽顶炽热熔体溅出，戴防烫面罩',
      '气路高压，维修必须释压',
    ],
    troubleshooting: [
      { symptom: '打壳频次降至 < 4 次/h', action: '气路压力不足或电磁阀卡' },
      { symptom: '下料量不准', action: '料斗结块 + 称重传感器漂移' },
      { symptom: '气缸异响', action: '密封圈漏气或活塞磨损' },
    ],
  },

  'CTRL-401': {
    overview:
      '直流母线监控柜 CTRL-401 集中监控系列电流、母线温度、各槽状态、整流参数等。' +
      '含 PLC、上位机、安全联锁、报警系统。是车间神经中枢。',
    operatingProcedure: [
      '每日核对报警历史和值班记录',
      '检查通讯指示灯（红色或闪烁立即排查）',
      '系统负载 30-70% 健康',
      '柜内温度 < 40°C，夏季注意通风',
    ],
    safetyNotes: [
      '柜内 380V 高压，非电工严禁开柜',
      '维护必须断电 + 挂牌',
      '严禁屏蔽报警联锁，特别是漏炉报警',
    ],
    troubleshooting: [
      { symptom: '通讯中断', action: '检查光纤、PLC 状态灯、终端电阻' },
      { symptom: '主电压 < 370V', action: '联系厂区配电班' },
      { symptom: '柜温过高', action: '清理风扇灰尘，检查空调' },
    ],
  },
};
